import React, { useState } from 'react';
import { logger } from '../../../lib/utils';
import { Facebook, Download, Loader2, Share2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import {
  getFacebookShareUrl,
  handleFacebookShare,
  getChallengePreviewImageUrl
} from '../challengeUtils';

/**
 * Social share buttons for challenge sharing.
 * One-click sharing via Facebook with OG-tagged URLs
 * so the scorecard image appears automatically as a rich preview.
 */
export function SocialShareButtons({ challengeCode }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showShareInstructions, setShowShareInstructions] = useState(false);
  const sharePageUrl = getFacebookShareUrl(challengeCode);

  const onFacebookClick = () => {
    handleFacebookShare(sharePageUrl);
  };

  const onDownloadChallenge = async () => {
    setIsProcessing(true);
    try {
      const imageUrl = getChallengePreviewImageUrl(challengeCode);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `challenge-${challengeCode}-scorecard.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowShareInstructions(false);
    } catch (error) {
      logger.error('Failed to download challenge:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-2 sm:space-y-3">
      <p className="text-[10px] sm:text-xs text-muted-foreground text-center font-medium px-2">
        Your Challenge Score Card and Link will be shared on Facebook
      </p>

      {/* Facebook share button */}
      <Button
        className="w-full h-10 sm:h-12 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium text-sm sm:text-base"
        onClick={onFacebookClick}
        data-testid="share-facebook-btn"
      >
        <Facebook className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
        Facebook
      </Button>

      {/* More sharing options - now opens instructions dialog */}
      <Button
        variant="outline"
        className="w-full h-9 sm:h-10 text-xs sm:text-sm"
        onClick={() => setShowShareInstructions(true)}
        data-testid="share-native-btn"
      >
        <Share2 className="h-4 w-4 mr-2 flex-shrink-0" />
        More sharing options...
      </Button>

      {/* Instructions dialog */}
      <Dialog open={showShareInstructions} onOpenChange={setShowShareInstructions}>
        <DialogContent
          className="sm:max-w-md w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-auto"
          data-testid="share-instructions-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Share Your Challenge
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-sm">
              <span className="block">To share your challenge on other platforms:</span>
              <span className="block">1. Tap <span className="font-semibold">Download</span> below to save your challenge scorecard.</span>
              <span className="block">2. Open the downloaded image from your device.</span>
              <span className="block">3. Tap <span className="font-semibold">Share</span> in your image viewer to send it to any app.</span>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowShareInstructions(false)}
              disabled={isProcessing}
              data-testid="share-instructions-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={onDownloadChallenge}
              disabled={isProcessing}
              data-testid="share-instructions-download-btn"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2 flex-shrink-0" />
              )}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
